import { useCallback, useState } from 'react';
import StylesProvider from '@mui/styles/StylesProvider';
import CssBaseline from '@mui/material/CssBaseline';
import { styled, StyledEngineProvider, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider } from 'styled-components';
import { theme } from '../../theme';
import { ConfirmDialog } from '../../shared/ConfirmDialog/ConfirmDialog';
import SideDrawer, { DRAWER_WIDTH } from './components/SideDrawer';
import TopBar from './components/TopBar';

const PREFIX = 'Main';
const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
  },
  [`& .${classes.content}`]: {
    flexGrow: 1,
    padding: theme.spacing(3),
    // The fixed app bar's space is padding, not margin, so it counts towards the min height.
    paddingTop: `calc(${theme.spacing(8)} + ${theme.spacing(3)})`,
    overflowWrap: 'anywhere',
    minHeight: '100dvh',
    backgroundColor: theme.palette.background.default,
  },
}));

interface LayoutProps {
  children: JSX.Element;
  renderSideDrawer?: boolean;
}

const Main = ({ children }: LayoutProps) => {
  const [drawerOpen, setDrawerOpen] = useState(true);

  // Stable: SideDrawer's close-on-mobile effect depends on it, and would otherwise re-close the drawer each render.
  const handleDrawerToggle = useCallback((open?: boolean) => {
    if (typeof open == 'boolean') {
      setDrawerOpen(open);
    } else {
      setDrawerOpen((curr) => !curr);
    }
    // Dispatch window resize event for MapBox to resize map correctly after drawer's collapsed
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);
  }, []);

  return (
    <StylesProvider injectFirst>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={theme}>
          <ThemeProvider theme={theme}>
            <Root>
              <CssBaseline />
              <TopBar handleDrawerToggle={handleDrawerToggle} isOpen={drawerOpen} />
              <SideDrawer handleDrawerToggle={handleDrawerToggle} isOpen={drawerOpen} />
              <StyledMain drawerOpen={drawerOpen} className={classes.content}>
                {children}
              </StyledMain>
            </Root>
            {/* Inside the theme: above it the confirm dialog fell back to MUI's default palette. */}
            <ConfirmDialog />
          </ThemeProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </StylesProvider>
  );
};

const StyledMain = styled('main')<{ drawerOpen: boolean }>`
  ${({ theme }) => theme.breakpoints.up('md')} {
    transition: 0.1s margin-left;
    margin-left: ${({ drawerOpen }) => (drawerOpen ? `${DRAWER_WIDTH}px` : '0')};
  }
`;

export default Main;
